import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Container,
  Skeleton,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { usePageTitle } from '@/shared/context/PageTitleContext';
import DetailPageSkeleton from './skeletons/DetailPageSkeleton';

const fillBodySx = {
  flex: '1 1 0',
  minHeight: 0,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  '& > *': {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    overflow: 'hidden',
  },
  '& > .MuiPaper-root': {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
} as const;

export interface DetailPageProps {
  title: string;
  /**
   * AppBar / document title. Defaults to `title` (the page h1).
   * Use when chrome should stay generic while the heading shows the record name.
   */
  pageTitle?: string;
  subtitle?: React.ReactNode;
  status?: React.ReactNode;
  backTo?: string;
  onBack?: () => void;
  /** Hide the outlined back button. Default shows it. */
  showBack?: boolean;
  actions?: React.ReactNode;
  loading?: boolean;
  /**
   * Skeleton the page title (h1 + AppBar) instead of a fallback string that
   * will be replaced when data arrives.
   */
  titleLoading?: boolean;
  /** Replaces the default detail skeleton when `loading` is true. */
  loadingFallback?: React.ReactNode;
  error?: string | null;
  notFound?: boolean;
  notFoundMessage?: string;
  maxWidth?: 'md' | 'lg';
  /**
   * Fill leftover viewport height so children (e.g. ListTable) can stretch
   * and scroll inside. Default keeps document flow (page scroll).
   */
  fillHeight?: boolean;
  children: React.ReactNode;
}

const DetailPage: React.FC<DetailPageProps> = ({
  title,
  pageTitle,
  subtitle,
  status,
  backTo,
  onBack,
  showBack = true,
  actions,
  loading = false,
  titleLoading = false,
  loadingFallback,
  error,
  notFound = false,
  notFoundMessage = 'Không tìm thấy dữ liệu.',
  maxWidth = 'lg',
  fillHeight = false,
  children,
}) => {
  const navigate = useNavigate();
  usePageTitle(
    titleLoading && !pageTitle ? undefined : (pageTitle ?? title),
    Boolean(titleLoading && !pageTitle)
  );

  const handleBack = (): void => {
    if (onBack) {
      onBack();
      return;
    }
    if (backTo) {
      navigate(backTo);
      return;
    }
    navigate(-1);
  };

  return (
    <Box
      sx={
        fillHeight
          ? {
              flex: '1 1 0',
              width: '100%',
              alignSelf: 'stretch',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }
          : undefined
      }
    >
      <Container
        maxWidth={maxWidth}
        sx={
          fillHeight
            ? {
                flex: '1 1 0',
                width: '100%',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                pt: 4,
              }
            : undefined
        }
      >
        <Box
          sx={
            fillHeight
              ? {
                  flex: '1 1 0',
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }
              : { mt: 4, mb: 4 }
          }
        >
        {showBack ? (
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ mb: 2, flexShrink: 0 }}
          >
            Quay lại
          </Button>
        ) : null}

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: 2,
            mb: fillHeight ? 1 : 3,
            flexShrink: 0,
          }}
        >
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              {titleLoading ? (
                <Skeleton variant="text" width={280} height={40} />
              ) : (
                <Typography variant="h4" component="h1">
                  {title}
                </Typography>
              )}
              {titleLoading && !status ? (
                <Skeleton variant="rounded" width={72} height={24} />
              ) : (
                status
              )}
            </Box>
            {titleLoading && subtitle == null ? (
              <Skeleton variant="text" width={200} height={20} sx={{ mt: 0.5 }} />
            ) : subtitle ? (
              <Typography component="div" variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
          {titleLoading && !actions ? (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Skeleton variant="rounded" width={96} height={32} />
              <Skeleton variant="circular" width={32} height={32} />
            </Box>
          ) : actions ? (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>{actions}</Box>
          ) : null}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          fillHeight ? (
            <Box sx={fillBodySx}>{loadingFallback ?? <DetailPageSkeleton />}</Box>
          ) : (
            (loadingFallback ?? <DetailPageSkeleton />)
          )
        ) : notFound ? (
          <Alert severity="warning">{notFoundMessage}</Alert>
        ) : fillHeight ? (
          <Box sx={fillBodySx}>{children}</Box>
        ) : (
          children
        )}
        </Box>
      </Container>
    </Box>
  );
};

export default DetailPage;
